import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { AiOutlineSearch } from 'react-icons/ai';

import RequestField from '../request-field';

/**
 * Picker for the clients or suppliers linked to the logged-in agent. The
 * options come from the agent-scoped endpoints, so the list is already
 * filtered by the backend.
 */
const AgentEntityAutocomplete = (props) => {
  const {
    fetchOptions,
    labelKey,
    tLabel,
    required,
    tPlaceholder,
    tHelper,
    tError,
    hasError,
    tEmptyMessage,
    value,
    onSelect,
  } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      try {
        const result = await fetchOptions();

        if (active) {
          setOptions(result || []);
        }
      } catch (error) {
        if (active) {
          enqueueSnackbar(t('something-went-wrong'), { variant: 'error' });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noOptionsText = useMemo(() => {
    if (loading) return t('loading');

    return options.length === 0 && tEmptyMessage
      ? t(tEmptyMessage)
      : t('no-results');
  }, [loading, options.length, tEmptyMessage, t]);

  const getOptionName = (option) =>
    `${option[labelKey] || ''}${
      option.santec_account_id ? ` (${option.santec_account_id})` : ''
    }`;

  const getOptionLabel = (option) => {
    const name = getOptionName(option);

    return option.email ? `${name} · ${option.email}` : name;
  };

  return (
    <RequestField
      tLabel={tLabel}
      required={required}
      tHelper={tHelper}
      tError={tError}
      hasError={hasError}
    >
      <Autocomplete
        className="request-field-control"
        options={options}
        loading={loading}
        value={value || null}
        noOptionsText={noOptionsText}
        getOptionLabel={getOptionLabel}
        getOptionSelected={(option, selected) => option.id === selected?.id}
        onChange={(event, selected) => onSelect(selected)}
        renderOption={(option) => (
          <div className="request-autocomplete-option">
            <span className="request-autocomplete-option-name">
              {getOptionName(option)}
            </span>
            {option.email && (
              <span className="request-autocomplete-option-email">
                {option.email}
              </span>
            )}
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            error={hasError}
            placeholder={t(tPlaceholder)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AiOutlineSearch />
                  )}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </RequestField>
  );
};

export default AgentEntityAutocomplete;
